import { useCallback, useEffect, useMemo, useRef } from 'react';

import { IServerSideGetRowsParams } from 'ag-grid-community';
import { set } from 'lodash';

import { EXPORT_ENUMS } from '@/constants';
import { getOperator } from '@/utils/getOperator';

interface IDataSourceProps {
  query: (args: Record<string, unknown>) => {
    unwrap: () => Promise<{ semen: { items: unknown[]; totalCount: number } }>;
  };
  variables?: Record<string, unknown>;
  skip?: boolean;
  searchBy: string;
  searchTerm: string;
  ownerId?: string;
  fertSiteId?: string;
  exportId?: number;
  filterActiveSemen?: boolean;
  filterByRanch?: boolean;
  filterBySite?: boolean;
  filterByExport?: boolean;
}

interface WhereClause {
  [key: string]: unknown;
  and?: Array<Record<string, unknown>>;
  or?: Array<Record<string, unknown>>;
  sexed?: { eq: boolean | null };
  quantity?: { eq: number };
  concentration?: { eq: number | null };
  cost?: { eq: number };
  sex?: { eq: number };
}

function buildFieldFilter(field: string, condition: Record<string, unknown>) {
  return { [field]: condition };
}

function buildBlankFilter(field: string, isTextFilter: boolean) {
  if (isTextFilter) {
    return {
      or: [
        buildFieldFilter(field, { eq: null }),
        buildFieldFilter(field, { eq: '' }),
      ],
    };
  }

  return buildFieldFilter(field, { eq: null });
}

function buildNotBlankFilter(field: string, isTextFilter: boolean) {
  if (isTextFilter) {
    return {
      and: [
        buildFieldFilter(field, { neq: null }),
        buildFieldFilter(field, { neq: '' }),
      ],
    };
  }

  return buildFieldFilter(field, { neq: null });
}

function buildSetFilter(field: string, values: unknown[]) {
  const nonNullValues = values.filter((value) => value != null);
  const includesNull = nonNullValues.length !== values.length;

  if (includesNull && nonNullValues.length === 0) {
    return buildFieldFilter(field, { eq: null });
  }

  if (includesNull) {
    return {
      or: [
        buildFieldFilter(field, { eq: null }),
        buildFieldFilter(field, { in: nonNullValues }),
      ],
    };
  }

  return buildFieldFilter(field, { in: nonNullValues });
}

export function useAdvancedSemenDataSource({
  query,
  variables,
  skip,
  searchBy,
  searchTerm,
  ownerId,
  fertSiteId,
  exportId,
  filterActiveSemen,
  filterByRanch,
  filterBySite,
  filterByExport,
}: IDataSourceProps) {
  const apiRef = useRef<{ api: IServerSideGetRowsParams['api'] } | null>(null);

  const filtersRef = useRef({
    searchBy,
    searchTerm,
    ownerId,
    fertSiteId,
    exportId,
    filterActiveSemen,
    skip,
    variables,
  });
  useEffect(() => {
    filtersRef.current = {
      searchBy,
      searchTerm,
      ownerId,
      fertSiteId,
      exportId,
      filterActiveSemen,
      skip,
      variables,
    };
  }, [
    searchBy,
    searchTerm,
    ownerId,
    fertSiteId,
    exportId,
    filterActiveSemen,
    skip,
    variables,
  ]);

  const generationRef = useRef(0);

  const getRows = useCallback(
    ({ request, success, fail, api }: IServerSideGetRowsParams) => {
      const {
        searchBy,
        searchTerm,
        ownerId,
        fertSiteId,
        exportId,
        filterActiveSemen,
        skip,
        variables,
      } = filtersRef.current;

      if (
        skip ||
        typeof request.startRow === 'undefined' ||
        typeof request.endRow === 'undefined'
      ) {
        success({
          rowData: [],
          rowCount: 0,
        });
        return;
      }

      apiRef.current = { api };
      const gen = generationRef.current;

      const order = request.sortModel
        .map((s) => ({
          sort: s.sort,
          field: api.getColumn(s.colId)?.getColDef().field,
        }))
        .filter((s) => s.field)
        .map((s) => set({}, s.field!, s.sort === 'asc' ? 'ASC' : 'DESC'));

      const filterModel = request.filterModel;
      const filterConditions: Record<string, unknown>[] = [];

      Object.entries(filterModel || {}).forEach(([field, condition]) => {
        const typedCondition = condition as Record<string, unknown>;
        const filterType = typedCondition.filterType as string | undefined;
        const type = typedCondition.type as string | undefined;
        const [parent, child] = field.split('.');

        if (filterType === 'set' && Array.isArray(typedCondition.values)) {
          filterConditions.push(buildSetFilter(field, typedCondition.values));
          return;
        }

        if (type === 'blank') {
          filterConditions.push(buildBlankFilter(field, filterType === 'text'));
          return;
        }

        if (type === 'notBlank') {
          filterConditions.push(
            buildNotBlankFilter(field, filterType === 'text'),
          );
          return;
        }

        if (parent === 'storageLocation') {
          if (child === 'site') {
            filterConditions.push({
              storageLocation: {
                site: {
                  name: {
                    [getOperator(type ?? '')]: typedCondition.filter,
                  },
                },
              },
            });
          } else {
            filterConditions.push({
              storageLocation: {
                name: {
                  [getOperator(type ?? '')]: typedCondition.filter,
                },
              },
            });
          }
          return;
        }

        if (parent === 'owner') {
          filterConditions.push({
            owner: {
              code: {
                [getOperator(type ?? '')]: typedCondition.filter,
              },
            },
          });
          return;
        }

        if (field === 'export') {
          const mapped =
            EXPORT_ENUMS[typedCondition.filter as keyof typeof EXPORT_ENUMS];
          if (mapped !== undefined) {
            filterConditions.push({
              export: {
                eq: mapped,
              },
            });
          }
          return;
        }
        filterConditions.push(
          child
            ? {
                [parent]: {
                  [child]: {
                    [getOperator(type ?? '')]: typedCondition.filter,
                  },
                },
              }
            : {
                [field]: {
                  [getOperator(type ?? '')]: typedCondition.filter,
                },
              },
        );
      });

      const searchByRoot = searchBy.includes('.')
        ? null
        : { [searchBy]: { contains: searchTerm } };
      const searchByParts = searchBy.split('.');

      let searchByNested = null;
      if (searchByParts.length === 3) {
        if (
          searchByParts[0] === 'storageLocation' &&
          searchByParts[1] === 'site' &&
          searchByParts[2] === 'name'
        ) {
          searchByNested = {
            storageLocation: {
              and: [{ site: { name: { contains: searchTerm } } }],
            },
          };
        } else {
          searchByNested = {
            [searchByParts[0]]: {
              and: [
                {
                  [searchByParts[1]]: {
                    [searchByParts[2]]: { contains: searchTerm },
                  },
                },
              ],
            },
          };
        }
      } else if (searchByParts.length === 2) {
        searchByNested = {
          [searchByParts[0]]: {
            and: [{ [searchByParts[1]]: { contains: searchTerm } }],
          },
        };
      }

      const where: WhereClause =
        searchBy.length === 0
          ? {
              or: [
                { name: { contains: searchTerm } },
                { cane: { contains: searchTerm } },
                { storageLocation: { name: { contains: searchTerm } } },
                { colCode: { contains: searchTerm } },
                {
                  storageLocation: { site: { name: { contains: searchTerm } } },
                },
                { soldTo: { contains: searchTerm } },
                { owner: { code: { contains: searchTerm } } },
              ],
            }
          : { ...(searchByRoot ?? searchByNested) };

      if (
        searchBy === 'quantity' ||
        searchBy === 'concentration' ||
        searchBy === 'cost' ||
        searchBy === 'sex'
      ) {
        const numValue = parseFloat(searchTerm);
        if (!Number.isNaN(numValue)) {
          where[searchBy] = { eq: numValue };
        }
      }

      if (searchBy === 'sexed') {
        const boolValue = searchTerm.toLowerCase();
        if (boolValue === 'true' || boolValue === 'false') {
          where.sexed = { eq: boolValue === 'true' };
        }
      }

      const finalWhere = {
        and: [
          filterActiveSemen ? { quantity: { eq: 0 } } : { quantity: { gt: 0 } },
          { stateCode: { eq: 0 } },
          ...filterConditions,
          where,
          ownerId ? { ownerId: { eq: ownerId } } : null,
          fertSiteId
            ? { storageLocation: { siteId: { eq: fertSiteId } } }
            : null,
          exportId ? { export: { eq: exportId } } : null,
        ].filter(Boolean),
      };

      query({
        skip: request.startRow,
        take: request.endRow - request.startRow,
        order,
        where: finalWhere,
        ...variables,
      })
        .unwrap()
        .then((res) => {
          if (generationRef.current !== gen) return;
          success({
            rowData: res.semen.items,
            rowCount: res.semen.totalCount,
          });
        })
        .catch(() => {
          if (generationRef.current !== gen) return;
          fail();
        });
    },
    [query],
  );

  const refetch = useCallback(() => {
    if (apiRef.current?.api) {
      generationRef.current++;
      apiRef.current.api.refreshServerSide({ purge: true });
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [
    searchBy,
    searchTerm,
    refetch,
    ownerId,
    fertSiteId,
    exportId,
    filterActiveSemen,
    filterByRanch,
    filterBySite,
    filterByExport,
  ]);

  return useMemo(
    () => ({
      dataSource: { getRows },
      refetch,
    }),
    [getRows, refetch],
  );
}
